/**
 * KalkulatorDuit Event Tracking System
 * 
 * Centralized analytics helper for consistent event tracking across the app.
 * This module provides reusable functions for tracking:
 * - Calculator events (view, start, calculate, CTA clicks)
 * - Waitlist events (page view, CTA clicks, form submissions)
 * - AI Budget Planner CTA clicks
 * 
 * Usage:
 *   import { trackCalculatorEvent, trackWaitlistEvent, trackCTAEvent } from '@/lib/eventTracking';
 * 
 * Event Naming Convention: snake_case (e.g., calculator_view, waitlist_submit)
 * Parameter Naming Convention: snake_case (e.g., page_name, calculator_slug)
 */

// =============================================================================
// CONFIGURATION
// =============================================================================

const DEBUG_MODE = process.env.NODE_ENV === 'development';

/**
 * Log event to console in development mode
 */
function debugLog(eventName, params) {
  if (DEBUG_MODE || (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('debugEvents') === '1')) {
    console.log(`[EventTracking] ${eventName}`, params);
  }
}

// =============================================================================
// CORE EVENT TRACKING
// =============================================================================

/**
 * Core event tracking function
 * Sends events to both Google Analytics and PostHog
 * 
 * @param {string} eventName - snake_case event name
 * @param {Object} params - Event parameters
 */
export function trackEvent(eventName, params = {}) {
  // Add timestamp
  const eventData = {
    ...params,
    timestamp: new Date().toISOString()
  };

  debugLog(eventName, eventData);

  // Send to Google Analytics (gtag)
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, eventData);
  }

  // Send to PostHog
  if (typeof window !== 'undefined' && window.posthog) {
    window.posthog.capture(eventName, eventData);
  }

  // Send to internal analytics API (non-blocking)
  sendToInternalAPI(eventName, eventData);
}

/**
 * Send event to internal analytics API
 * Non-blocking, fire-and-forget
 */
async function sendToInternalAPI(eventName, params) {
  const API_URL = process.env.REACT_APP_BACKEND_URL || '';
  
  try {
    await fetch(`${API_URL}/api/analytics/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event: eventName,
        metadata: params,
        path: typeof window !== 'undefined' ? window.location.pathname : null,
        timestamp: params.timestamp
      }),
      keepalive: true
    });
  } catch {
    // Silent fail - analytics should not break the app
  }
}

// =============================================================================
// CALCULATOR EVENTS
// =============================================================================

/**
 * Calculator event types
 */
export const CALCULATOR_EVENTS = {
  VIEW: 'calculator_view',
  START: 'calculator_start',
  CALCULATE: 'calculator_calculate',
  RESULT_CTA_CLICK: 'calculator_result_cta_click'
};

/**
 * Create base calculator params
 * @param {Object} calculatorMeta - Calculator metadata
 * @returns {Object} Base params for calculator events
 */
function createCalculatorBaseParams(calculatorMeta) {
  return {
    page_name: calculatorMeta.title || calculatorMeta.slug,
    page_type: 'calculator_detail',
    calculator_slug: calculatorMeta.slug,
    calculator_name: calculatorMeta.name || calculatorMeta.title,
    calculator_category: calculatorMeta.category
  };
}

/**
 * Track calculator-related events
 * 
 * @param {'view'|'start'|'calculate'|'result_cta_click'} action - The action type
 * @param {Object} calculatorMeta - Calculator metadata
 * @param {Object} extraParams - Additional parameters
 * 
 * @example
 * // Track calculator view
 * trackCalculatorEvent('view', { slug: 'kpr', name: 'KPR Calculator', category: 'loans' });
 * 
 * // Track calculator start (first interaction)
 * trackCalculatorEvent('start', calculatorMeta);
 * 
 * // Track calculation completed
 * trackCalculatorEvent('calculate', calculatorMeta, { has_result: true });
 * 
 * // Track CTA click in results
 * trackCalculatorEvent('result_cta_click', calculatorMeta, { cta_name: 'copy', cta_location: 'result_section' });
 */
export function trackCalculatorEvent(action, calculatorMeta, extraParams = {}) {
  const baseParams = createCalculatorBaseParams(calculatorMeta);
  
  let eventName;
  let params = { ...baseParams };

  switch (action) {
    case 'view':
      eventName = CALCULATOR_EVENTS.VIEW;
      break;
      
    case 'start':
      eventName = CALCULATOR_EVENTS.START;
      break;
      
    case 'calculate':
      eventName = CALCULATOR_EVENTS.CALCULATE;
      params.has_result = extraParams.has_result ?? true;
      break;
      
    case 'result_cta_click':
      eventName = CALCULATOR_EVENTS.RESULT_CTA_CLICK;
      params.cta_name = extraParams.cta_name || 'unknown';
      params.cta_location = extraParams.cta_location || 'result_section';
      break;
      
    default:
      console.warn(`[EventTracking] Unknown calculator action: ${action}`);
      return;
  }

  // Merge any remaining extra params
  const { has_result, cta_name, cta_location, ...otherParams } = extraParams;
  params = { ...params, ...otherParams };

  trackEvent(eventName, params);
}

// =============================================================================
// WAITLIST EVENTS
// =============================================================================

/**
 * Waitlist event types
 */
export const WAITLIST_EVENTS = {
  PAGE_VIEW: 'waitlist_page_view',
  CTA_CLICK: 'waitlist_cta_click',
  SUBMIT: 'waitlist_submit',
  SUBMIT_SUCCESS: 'waitlist_submit_success',
  SUBMIT_ERROR: 'waitlist_submit_error'
};

/**
 * Track waitlist-related events
 * 
 * @param {'page_view'|'cta_click'|'submit'|'submit_success'|'submit_error'} action - The action type
 * @param {Object} params - Event parameters
 * 
 * @example
 * // Track waitlist page view
 * trackWaitlistEvent('page_view', { page_name: 'template_excel' });
 * 
 * // Track CTA click
 * trackWaitlistEvent('cta_click', { 
 *   page_name: 'template_excel',
 *   cta_name: 'hero_cta',
 *   cta_location: 'hero_section'
 * });
 * 
 * // Track form submission
 * trackWaitlistEvent('submit', { 
 *   page_name: 'template_excel',
 *   form_name: 'template_waitlist',
 *   focus_area: 'budget'
 * });
 * 
 * // Track submission success
 * trackWaitlistEvent('submit_success', { 
 *   page_name: 'template_excel',
 *   form_name: 'template_waitlist',
 *   focus_area: 'budget'
 * });
 * 
 * // Track submission error
 * trackWaitlistEvent('submit_error', { 
 *   page_name: 'template_excel',
 *   form_name: 'template_waitlist',
 *   error_type: 'network_error',
 *   focus_area: 'budget'
 * });
 */
export function trackWaitlistEvent(action, params = {}) {
  const baseParams = {
    page_name: params.page_name || 'template_excel',
    page_type: 'landing_page'
  };

  let eventName;
  let eventParams = { ...baseParams };

  switch (action) {
    case 'page_view':
      eventName = WAITLIST_EVENTS.PAGE_VIEW;
      break;
      
    case 'cta_click':
      eventName = WAITLIST_EVENTS.CTA_CLICK;
      eventParams.cta_name = params.cta_name || 'unknown';
      eventParams.cta_location = params.cta_location || 'unknown';
      break;
      
    case 'submit':
      eventName = WAITLIST_EVENTS.SUBMIT;
      eventParams.form_name = params.form_name || 'template_waitlist';
      if (params.focus_area) eventParams.focus_area = params.focus_area;
      break;
      
    case 'submit_success':
      eventName = WAITLIST_EVENTS.SUBMIT_SUCCESS;
      eventParams.form_name = params.form_name || 'template_waitlist';
      if (params.focus_area) eventParams.focus_area = params.focus_area;
      break;
      
    case 'submit_error':
      eventName = WAITLIST_EVENTS.SUBMIT_ERROR;
      eventParams.form_name = params.form_name || 'template_waitlist';
      eventParams.error_type = params.error_type || 'unknown';
      if (params.focus_area) eventParams.focus_area = params.focus_area;
      break;
      
    default:
      console.warn(`[EventTracking] Unknown waitlist action: ${action}`);
      return;
  }

  trackEvent(eventName, eventParams);
}

// =============================================================================
// CTA EVENTS
// =============================================================================

/**
 * Track general CTA clicks
 * 
 * @param {Object} params - CTA parameters
 * @param {string} params.cta_name - Name/identifier of the CTA
 * @param {string} params.source_page - Page where CTA was clicked
 * @param {string} params.source_section - Section of the page
 * @param {string} [params.destination] - Where CTA leads to (optional)
 * 
 * @example
 * trackCTAEvent({
 *   cta_name: 'try_ai_budget_planner',
 *   source_page: 'calculator_result',
 *   source_section: 'advanced_cta'
 * });
 */
export function trackCTAEvent(params) {
  trackEvent('cta_click', {
    cta_name: params.cta_name,
    source_page: params.source_page,
    source_section: params.source_section,
    destination: params.destination
  });
}

/**
 * Track AI Budget Planner CTA clicks specifically
 * 
 * @param {string} sourcePage - Page where CTA was clicked
 * @param {string} sourceSection - Section of the page
 * @param {string} [ctaName] - Custom CTA name (defaults to 'ai_budget_planner')
 * 
 * @example
 * trackAIBudgetPlannerClick('home', 'hero_section');
 * trackAIBudgetPlannerClick('calculator_result', 'advanced_version_cta');
 */
export function trackAIBudgetPlannerClick(sourcePage, sourceSection, ctaName = 'ai_budget_planner') {
  trackEvent('ai_budget_planner_click', {
    source_page: sourcePage,
    source_section: sourceSection,
    cta_name: ctaName
  });
}

// =============================================================================
// SESSION TRACKING HELPERS
// =============================================================================

/**
 * Create a session-based tracker to fire events only once per session
 * Useful for tracking 'start' events that should only fire once
 * 
 * @param {string} eventKey - Unique key for this event
 * @returns {Object} Object with hasFired and markFired methods
 * 
 * @example
 * const startTracker = createSessionTracker('calc_kpr_start');
 * 
 * // On first interaction
 * if (!startTracker.hasFired()) {
 *   trackCalculatorEvent('start', calculatorMeta);
 *   startTracker.markFired();
 * }
 */
export function createSessionTracker(eventKey) {
  const storageKey = `_evt_${eventKey}`;
  
  return {
    hasFired: () => {
      if (typeof window === 'undefined') return false;
      return sessionStorage.getItem(storageKey) === '1';
    },
    markFired: () => {
      if (typeof window !== 'undefined') {
        sessionStorage.setItem(storageKey, '1');
      }
    },
    reset: () => {
      if (typeof window !== 'undefined') {
        sessionStorage.removeItem(storageKey);
      }
    }
  };
}

// =============================================================================
// EXPORTS
// =============================================================================

export default {
  // Core
  trackEvent,
  
  // Calculator
  trackCalculatorEvent,
  CALCULATOR_EVENTS,
  
  // Waitlist
  trackWaitlistEvent,
  WAITLIST_EVENTS,
  
  // CTAs
  trackCTAEvent,
  trackAIBudgetPlannerClick,
  
  // Helpers
  createSessionTracker
};
